import React, { useState } from 'react';
import { Columns3, Download, Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  DEFAULT_PUNCH_LIST_CSV_COLUMN_KEYS,
  type PunchListCsvColumn,
  type PunchListCsvColumnKey,
} from '../types';

interface PunchListExportDialogProps {
  columns: PunchListCsvColumn[];
  initialSelectedColumns: PunchListCsvColumnKey[];
  exporting: boolean;
  onOpenChange: (open: boolean) => void;
  onExport: (selectedColumns: PunchListCsvColumnKey[]) => Promise<void> | void;
}

export const PunchListExportDialog: React.FC<PunchListExportDialogProps> = ({
  columns,
  initialSelectedColumns,
  exporting,
  onOpenChange,
  onExport,
}) => {
  const [selected, setSelected] = useState(() => new Set(initialSelectedColumns));
  const allSelected = columns.length > 0 && columns.every(({ key }) => selected.has(key));
  const columnsByKey = new Map(columns.map((column) => [column.key, column]));
  const defaultColumns = DEFAULT_PUNCH_LIST_CSV_COLUMN_KEYS.flatMap((key) => {
    const column = columnsByKey.get(key);
    return column ? [column] : [];
  });
  const defaultColumnKeys = new Set(defaultColumns.map(({ key }) => key));
  const optionalColumns = columns.filter(({ key }) => !defaultColumnKeys.has(key));
  const leftDefaults = defaultColumns.slice(0, 4);
  const rightDefaults = defaultColumns.slice(4, 8);
  const displayColumns = [
    ...leftDefaults.flatMap((column, index) => [column, rightDefaults[index]].filter(Boolean)),
    ...optionalColumns,
  ];

  const setColumnSelected = (column: PunchListCsvColumnKey, checked: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(column);
      else next.delete(column);
      return next;
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && exporting) return;
    onOpenChange(open);
  };

  const handleExport = () => {
    const selectedInDisplayOrder = columns
      .filter(({ key }) => selected.has(key))
      .map(({ key }) => key);
    void onExport(selectedInDisplayOrder);
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Columns3 className="h-5 w-5 text-primary" strokeWidth={1.9} />
            Choose CSV columns
          </DialogTitle>
          <DialogDescription>
            Select the columns to include. Your choices will be remembered for your next export.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-hidden rounded-[13px] border border-border bg-muted/40">
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
            <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
              {selected.size} of {columns.length} selected
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() =>
                setSelected(allSelected ? new Set() : new Set(columns.map(({ key }) => key)))
              }
              disabled={exporting}
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </Button>
          </div>
          <div className="grid gap-px bg-border sm:grid-cols-2">
            {displayColumns.map((column) => {
              const checkboxId = `punch-list-export-${column.key}`;
              return (
                <label
                  key={column.key}
                  htmlFor={checkboxId}
                  className="flex cursor-pointer items-center gap-3 bg-card px-4 py-3 text-sm font-medium transition-colors hover:bg-accent"
                >
                  <Checkbox
                    id={checkboxId}
                    checked={selected.has(column.key)}
                    onCheckedChange={(checked) => setColumnSelected(column.key, checked === true)}
                    disabled={exporting}
                  />
                  {column.label}
                </label>
              );
            })}
          </div>
        </div>

        {selected.size === 0 && (
          <p className="text-sm text-destructive">Select at least one column to export.</p>
        )}

        <DialogFooter className="gap-2 sm:space-x-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={exporting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleExport}
            disabled={selected.size === 0 || exporting}
            className="gap-2"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Export CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PunchListExportDialog;
